import React, { useEffect } from 'react';
import { ArrowForwardIcon, WarningIcon } from '@chakra-ui/icons';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Flex,
  Heading,
  Spinner,
  Text,
  useBreakpointValue,
  useToast,
} from '@chakra-ui/react';
import { FieldArrayWithId, useFieldArray } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { SolarSourceIcon } from 'clipsal-cortex-icons/src/custom-appliance-icons';
import { ChannelData } from 'clipsal-cortex-types/src/api/api-ww-meter';

import { MeterCTSelect } from '../../../../common/components/MeterCTSelect';
import { SIDENAV_WIDTH } from '../../../../common/constants';
import { useSite } from '../../siteApi';
import { selectRawMeters } from '../meterSetupSlice';
import { CTConfigFormData } from './ct-configuration-form-types';
import { COMMON_NEXT_BUTTON_PROPS } from './ct-configuration-helpers';
import { TabComponentProps } from './CTConfigurationContainer';
import { FieldIconWrapper } from './FieldIconWrapper';
import { EmptySelectedCircuitsCard, SelectedCircuitsCard } from './SelectedCircuitsCard';
import { useReservedCTsForSolar } from './useReservedCTsForSolar';

export function SolarCTConfiguration(props: TabComponentProps) {
  const { control, errors, updateCTFunctions, isUpdatingCTs, circuitState } = props;

  const { fields } = useFieldArray({
    name: `solar` as const,
    control,
  });

  const isMobileViewport = useBreakpointValue(
    {
      base: true,
      xl: false,
    },
    { ssr: false }
  );

  return (
    <Flex direction="column" data-testid="solar-tab-panel">
      <Accordion defaultIndex={0} allowToggle>
        {fields.map((field, index) => {
          const errorsAtIndex = errors?.solar?.[index];
          return (
            <AccordionItem key={`${field.id}-${index}`}>
              <AccordionButton role={'button'} as={Box}>
                <Flex w={'100%'} justify={'space-between'} align={'center'}>
                  <Flex align="center">
                    <Heading size="sm" fontWeight="500">
                      Solar {index + 1}
                    </Heading>
                    {errorsAtIndex && <WarningIcon color={'red.500'} w={5} h={5} ml={2} />}
                  </Flex>
                  <Flex align={'center'}>
                    <AccordionIcon />
                  </Flex>
                </Flex>
              </AccordionButton>

              <AccordionPanel pb={4}>
                {errorsAtIndex && (
                  <Text color="red.500" mt={2}>
                    {errorsAtIndex?.circuits?.message}
                  </Text>
                )}
                <Text color="dusk.100" mb={8} mt={2}>
                  Select which CT's are measuring solar
                </Text>
                <NestedSolarCTConfigurationFieldArray field={field} solarApplianceIndex={index} {...props} />
              </AccordionPanel>
            </AccordionItem>
          );
        })}
      </Accordion>
      <Flex
        position={'fixed'}
        bottom={16}
        left={0}
        zIndex={99}
        width="100%"
        justifyContent={'center'}
        paddingLeft={isMobileViewport ? 0 : SIDENAV_WIDTH}
        pointerEvents="none"
      >
        <Flex width={isMobileViewport ? '100%' : '60%'}>
          <Button
            data-testid="submit-solar-button"
            aria-label="Submit Solar"
            rightIcon={isUpdatingCTs ? <Spinner size="sm" /> : <ArrowForwardIcon />}
            disabled={isUpdatingCTs || !!circuitState.pendingMeterIds.length}
            {...COMMON_NEXT_BUTTON_PROPS}
            onClick={() => updateCTFunctions['solar']()}
          >
            Next
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
}

export function NestedSolarCTConfigurationFieldArray({
  control,
  solarApplianceIndex,
  field,
  register,
  onCircuitChange,
  circuitState,
  errors,
  trigger,
}: TabComponentProps & {
  solarApplianceIndex: number;
  field: FieldArrayWithId<CTConfigFormData, 'solar', 'id'>;
}) {
  const rawMeters = useSelector(selectRawMeters);
  const { values } = circuitState;
  const { fields, append, remove } = useFieldArray({
    name: `solar.${solarApplianceIndex}.circuits` as 'solar.0.circuits',
    control,
  });
  const { site } = useSite();
  const toast = useToast();
  const errorsAtIndex = errors?.solar?.[solarApplianceIndex];

  useEffect(() => {
    // show toast errors as the form can be lengthier
    // and will make it harder to spot error messages
    if (errorsAtIndex?.circuits) {
      toast({
        title: `Missing CT in Solar ${solarApplianceIndex + 1}`,
        description: errorsAtIndex?.circuits?.message + ` ${solarApplianceIndex + 1}`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [errorsAtIndex, toast, solarApplianceIndex]);

  const reservedCts = useReservedCTsForSolar(control, solarApplianceIndex);

  function handleSelectCircuit(circuitData: ChannelData) {
    // returns index if found otherwise returns -1
    const indexOfCTField = fields.findIndex((selectedCT) => circuitData.ww_circuit_id === selectedCT.ww_circuit_id);

    if (indexOfCTField >= 0) {
      remove(indexOfCTField);
    } else {
      const channel = {
        ...circuitData,
        circuit_name: 'Solar Generation',
        clipsal_monitors: site.metering_configuration === 'NET' ? 'pv_site_net' : 'pv_site',
      };
      append(channel, { shouldFocus: false });
    }

    // revalidate form if there is any errors for solar tab
    if (errors?.solar) trigger(`solar.${solarApplianceIndex}.circuits` as 'solar.0.circuits');
  }

  return (
    <Box mt={4}>
      {Object.values(rawMeters).map((meter, idx) => {
        return (
          <MeterCTSelect
            meterIndex={idx}
            key={idx + field.id}
            meter={meter}
            onCTClick={handleSelectCircuit}
            selectedCTs={fields}
            reservedCTs={reservedCts}
          />
        );
      })}

      <Box mb={10}>
        {fields.map((field, idx) => {
          const switchValues = values.find((value) => value.circuitId === field.ww_circuit_id);
          if (!switchValues) throw new Error(`Unable to find switch value for circuit id ${field.ww_circuit_id}`);

          return (
            <SelectedCircuitsCard
              key={field.ww_circuit_id}
              source={`solar.${solarApplianceIndex}.circuits` as `solar.${number}.circuits`}
              field={field}
              index={idx}
              register={register}
              onDismiss={() => remove(idx)}
              iconComponent={
                <FieldIconWrapper>
                  <SolarSourceIcon h={12} w={12} />
                </FieldIconWrapper>
              }
              onCircuitChange={onCircuitChange}
              polarityValue={switchValues.polarity}
              voltageRefValue={switchValues.voltageReference}
              ctRating={switchValues.ctRating}
            />
          );
        })}
        {!fields.length && <EmptySelectedCircuitsCard iconComponent={<SolarSourceIcon h={12} w={12} />} />}
      </Box>
    </Box>
  );
}
